import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { fetchRagTexts } from '@/api/content/text'
import { contentSearchSchema } from '@/common/contentSearchSchema'
import { RagTextPage } from '@/components/RagTextPage'
import { isConversationWorkspaceAvailable } from '@/features/conversation/availability'
import { LegacyContentSettingsBridge } from '@/features/settings/LegacyContentSettingsBridge'

export const Route = createFileRoute('/_auth/$orgSlug/content/text/')({
  validateSearch: contentSearchSchema,
  loaderDeps: ({ search }) => ({ page: search.page, q: search.q, docId: search.docId }),
  loader: ({ deps, context }) =>
    isConversationWorkspaceAvailable(context.user.userOrgProfile?.enabled_features)
      ? Promise.resolve({ items: [], pageCount: 1, documentCount: 0 })
      : fetchRagTexts({ currentPage: deps.page, searchText: deps.q, docId: deps.docId }),
  head: () => ({ meta: [{ title: 'Global Content - Snowvault' }] }),
  component: TextPage
})

function TextPage() {
  const { orgSlug } = Route.useParams()
  const { items, pageCount, documentCount } = Route.useLoaderData()
  const { page, q, docId } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { userOrgProfile } = Route.useRouteContext().user
  const conversationWorkspaceAvailable = isConversationWorkspaceAvailable(userOrgProfile?.enabled_features)

  useEffect(() => {
    if (conversationWorkspaceAvailable) return
    if (items.length === 0 && page > 1) {
      void navigate({ search: { page: 1, q } })
    }
  }, [conversationWorkspaceAvailable, items.length, page, q, navigate])

  if (conversationWorkspaceAvailable) {
    return (
      <LegacyContentSettingsBridge
        orgSlug={orgSlug}
        kind="text"
        {...(docId !== undefined ? { contentItemId: docId } : {})}
      />
    )
  }

  return (
    <RagTextPage
      breadcrumbs={[{ label: 'Global Content' }, { label: 'Text' }]}
      items={items}
      currentPage={page}
      pageCount={pageCount}
      documentCount={documentCount}
      searchText={q}
      onPageChange={(p) => void navigate({ search: { page: p, q } })}
      onSearchChange={(text) => void navigate({ search: { q: text, page: 1 } })}
      onClearDocId={docId !== undefined ? () => void navigate({ search: { page: 1, q: '' } }) : undefined}
    />
  )
}
